/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { CallDirection, CallStatusFromBE } from '../../../call-manager/typings';
import { ReactComponent as CallIncomingIcon } from '../../../../shared/assets/svg/call-incoming.svg';
import { ReactComponent as CallNotConnectedIcon } from '../../../../shared/assets/svg/call-not-connected.svg';
import { ReactComponent as CallOutgoingIcon } from '../../../../shared/assets/svg/call-outgoing.svg';
import { ReactComponent as CallMissed } from '../../../../shared/assets/svg/call-missed.svg';

import { CallMessage } from '../typings/message-types';
import { TeamMember } from '../typings';

type CallStylesForChat = {
  icon: JSX.Element;
  title: string;
  titleColor: string;
  iconColor: string;
};

const defaultStyles = (size: number, icon: JSX.Element): CallStylesForChat => ({
  icon,
  title: 'Unknown status',
  titleColor: '#2d2d2d',
  iconColor: '#3D906D',
});

const getIncomingCallStylesForChatPreview = (
  status: CallStatusFromBE,
  size: number,
  duration?: number,
  agent?: TeamMember
): CallStylesForChat => {
  const icon = <CallIncomingIcon width={size} height={size} />;

  switch (status) {
    case CallStatusFromBE.INITIATED:
      return {
        icon,
        title: 'Incoming call',
        titleColor: '#616161',
        iconColor: '#616161',
      };
    case CallStatusFromBE.IN_PROGRESS:
      return {
        icon,
        title: 'Ongoing call',
        titleColor: '#616161',
        iconColor: '#616161',
      };
    case CallStatusFromBE.END:
      return {
        icon,
        title: 'Incoming call',
        titleColor: '#616161',
        iconColor: '#616161',
      };
    case CallStatusFromBE.MISSED:
    case CallStatusFromBE.FAILED:
      return {
        icon: <CallMissed width={size} height={size} />,
        title: 'Missed call',
        titleColor: '#EF5350',
        iconColor: '#EF5350',
      };
    case CallStatusFromBE.REJECT:
      return {
        icon,
        title: 'Declined call',
        titleColor: '#FF9800',
        iconColor: '#FF9800',
      };
    default:
      return defaultStyles(size, icon);
  }
};

const getOutgoingCallStylesForChatPreview = (
  status: CallStatusFromBE,
  size: number,
  duration?: number,
  agent?: TeamMember
): CallStylesForChat => {
  const icon = <CallOutgoingIcon width={size} height={size} />;

  switch (status) {
    case CallStatusFromBE.INITIATED:
      return {
        icon,
        title: 'Outgoing call',
        titleColor: '#616161',
        iconColor: '#616161',
      };
    case CallStatusFromBE.IN_PROGRESS:
      return {
        icon,
        title: 'Ongoing call',
        titleColor: '#616161',
        iconColor: '#616161',
      };
    case CallStatusFromBE.END:
      return {
        icon,
        title: 'Outgoing call',
        titleColor: '#616161',
        iconColor: '#616161',
      };
    case CallStatusFromBE.REJECT:
    case CallStatusFromBE.MISSED:
    case CallStatusFromBE.FAILED:
      return {
        icon: <CallNotConnectedIcon width={size} height={size} />,
        title: 'Not connected',
        titleColor: '#616161',
        iconColor: '#616161',
      };
    default:
      return defaultStyles(size, icon);
  }
};

export const getCallIConsAndStylesForChatPreview = ({
  callMessage,
  size = 16,
  agent,
}: {
  callMessage: CallMessage;
  size?: number;
  agent?: TeamMember;
}): CallStylesForChat => {
  const { callStatus: status, durationInSeconds: duration, direction } = callMessage;

  return direction === CallDirection.INCOMING
    ? getIncomingCallStylesForChatPreview(status, size, duration, agent)
    : getOutgoingCallStylesForChatPreview(status, size, duration, agent);
};
